import React, { useEffect } from 'react';
import ReauthenticateConsentPageContent from './ReauthenticateConsentPageContent';
import CreateConsentPageContent from './CreateConsentPageContent';
import { useAppState, useSelectedBank, useSelectedProvider } from '../../hooks/zustandHooks';
import IConsentConfigurationProps from '../../types/IConsentConfigurationProps';
import { useTranslation } from 'react-i18next';
import Container from '../container/Container';
import { exitApp } from '../../hooks/utils';
import { LogoHeader } from './LogoHeader';
import ConsentLegalInfo from './ConsentLegalInfo';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';
import { useSettings } from '../../hooks/queryHooks';

interface IProps {
  bankName: string;
  bankLogoUrl?: string;
  consentConfiguration?: IConsentConfigurationProps;
  isReauth: boolean;
}

const ConsentPageContent: React.FC<IProps> = ({ bankName, bankLogoUrl, consentConfiguration, isReauth }: IProps) => {
  const { t } = useTranslation();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const { settings } = useSettings();
  const providerIdFromUrl = urlSearchParams.get('provider_id');
  const bankCodeFromUrl = urlSearchParams.get('bank_code');
  const { appState, setAppState } = useAppState();
  const selectedAccountType = useSelectedProvider(state => state.selectedProvider?.selectedAccountType);
  const brandId = useSelectedBank(state => state.selectedBank?.brandId);

  const { clientId, redirectUrl } = appState;

  useEffect(() => {
    if (window.hj) {
      if (selectedAccountType) {
        window.hj('tagRecording', [`${selectedAccountType} Account`]);
      }

      if (clientId && brandId) {
        window.hj('identify', clientId, {
          brandId: brandId
        });
      }
    }
  }, [clientId, brandId, selectedAccountType]);

  const handleRedirect = async () => {
    if (redirectUrl) {
      trackEvent({
        type: EventTypes.CREATE_CONSENT_SUCCESS
      });
      window.location.replace(redirectUrl);
    }
  };

  return (
    <Container
      primaryBtn={{
        text: consentConfiguration?.isImplicit ? t('Continue') : t('Agree'),
        onClick: handleRedirect,
        disabled: !redirectUrl
      }}
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => {
            exitApp();
            setAppState({ redirectUrl: undefined });
          }
        }
      })}
    >
      <LogoHeader
        bankLogoUrl={bankLogoUrl}
        bankName={bankName}
        showBackButton={!(providerIdFromUrl || bankCodeFromUrl)}
      />

      {isReauth ? (
        <ReauthenticateConsentPageContent
          t={t}
          bankName={bankName}
          oldConsent={consentConfiguration?.oldConsentEndDate!}
          consentStatus={consentConfiguration?.consentStatus}
          isImplicit={consentConfiguration?.isImplicit}
        />
      ) : (
        <CreateConsentPageContent
          t={t}
          terms={consentConfiguration?.terms}
          bankName={bankName}
          isImplicit={consentConfiguration?.isImplicit}
        />
      )}
      <ConsentLegalInfo />
    </Container>
  );
};

export default ConsentPageContent;
